import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePlayerAuth } from '@/contexts/PlayerAuthContext';
import { toast } from 'sonner';
import api from '@/lib/api';
import { ArrowLeft, Check, X } from 'lucide-react';
import { UnconfirmedInboxView } from '@/components/auth/UnconfirmedInboxView';
import { ForgotPasswordView } from '@/components/auth/ForgotPasswordView';
import { AccessCodeSection } from '@/components/auth/AccessCodeSection';
import { AdminSignupFields, PlayerSignupFields, PasswordField, FieldInput } from '@/components/auth/AuthFormFields';
import { openSupportBubble } from '@/components/support/SupportBubble';

const FONT_H = { fontFamily: "'Outfit', sans-serif" };
const FONT_B = { fontFamily: "'Manrope', sans-serif" };
const PRIMARY = '#6C3CE0';
const GRADIENT_BG = 'linear-gradient(135deg, #E91E8C 0%, #6C3CE0 100%)';

/* ─── Password Strength ─── */
const PW_RULES = [
  { id: 'len', label: 'At least 8 characters', test: p => p.length >= 8 },
  { id: 'upper', label: 'One uppercase letter', test: p => /[A-Z]/.test(p) },
  { id: 'lower', label: 'One lowercase letter', test: p => /[a-z]/.test(p) },
  { id: 'num', label: 'One number', test: p => /\d/.test(p) },
  { id: 'special', label: 'One special character', test: p => /[^A-Za-z0-9]/.test(p) },
];

function getStrengthColor(pct) {
  if (pct <= 40) return '#ef4444';
  if (pct <= 60) return '#f59e0b';
  if (pct <= 80) return '#3b82f6';
  return '#22c55e';
}

function getStrengthLabel(pct) {
  if (pct <= 40) return 'Weak';
  if (pct <= 60) return 'Fair';
  if (pct <= 80) return 'Good';
  return 'Strong';
}

function PasswordStrength({ password }) {
  const passed = PW_RULES.filter(r => r.test(password)).length;
  if (!password) return null;
  const pct = (passed / PW_RULES.length) * 100;
  const color = getStrengthColor(pct);
  const label = getStrengthLabel(pct);

  return (
    <div className="mt-2 space-y-2" data-testid="password-strength">
      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
          <div className="h-full rounded-full transition-all duration-300" style={{ width: `${pct}%`, backgroundColor: color }} />
        </div>
        <span className="text-[10px] font-semibold" style={{ color }}>{label}</span>
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1">
        {PW_RULES.map(r => {
          const ok = r.test(password);
          return (
            <div key={r.id} className="flex items-center gap-1.5 text-[11px]" style={{ color: ok ? '#22c55e' : '#94a3b8' }}>
              {ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
              <span>{r.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function getSubmitButtonLabel({ loading, isLogin, captchaPending }) {
  if (loading) return 'Please wait...';
  if (captchaPending) return 'Verifying you\u2019re human\u2026';
  return isLogin ? 'Sign In' : 'Create Account';
}

function isPasswordStrong(p) {
  return PW_RULES.every(r => r.test(p));
}

// Captcha removed from all auth flows — bot protection is now handled at the
// Cloudflare WAF edge layer (Security → Bot Fight Mode / Under Attack Mode).
// captchaToken is kept as an empty string for backward-compat with the auth
// context signatures that still accept it; backend simply ignores the field.
const TURNSTILE_ENABLED = false;

function getSubmitHandler({ isLogin, role, handleUnifiedLogin, handleAdminSignup, handlePlayerSignup }) {
  if (isLogin) return handleUnifiedLogin;
  if (role === 'admin') return handleAdminSignup;
  return handlePlayerSignup;
}

/* ─── Main ─── */
export default function UnifiedAuthPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login: adminLogin, isAuthenticated: isAdminAuth, setUser: setAdminUser } = useAuth();
  const { login: playerLogin, isAuthenticated: isPlayerAuth, setPlayer } = usePlayerAuth();

  const [role, setRole] = useState(searchParams.get('tab') || 'player');
  const [mode, setMode] = useState(searchParams.get('mode') || 'login');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [unconfirmed, setUnconfirmed] = useState(null); // { email, role } when waiting for confirmation
  const [resending, setResending] = useState(false);

  const [adminForm, setAdminForm] = useState({ email: '', password: '', confirm_password: '', first_name: '', last_name: '', society_name: '' });
  const [playerForm, setPlayerForm] = useState({ email: '', password: '', confirm_password: '', first_name: '', last_name: '', display_name: '' });
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });

  // Soft-launch admin-invite gate. The backend tells us if the gate is on
  // (env var `ADMIN_INVITE_REQUIRED=true`). We auto-fill the code from
  // `?code=...` so an invite link like `/signup?code=SM-XXXX-YYYY` is
  // friction-free, but the field is also visible so a user who lost the
  // link can type/paste it manually.
  const [inviteRequired, setInviteRequired] = useState(false);
  const [inviteCode, setInviteCode] = useState(searchParams.get('code') || '');

  useEffect(() => {
    api.get('/auth/signup-config')
      .then(({ data }) => setInviteRequired(Boolean(data?.invite_required)))
      .catch(() => setInviteRequired(false));
  }, []);

  // Cloudflare Turnstile token. We use ONE token for whichever form is
  // currently visible (login / signup / forgot). The widget re-renders
  // and issues a fresh token whenever the user switches mode.
  const [captchaToken, setCaptchaToken] = useState('');

  const isLogin = mode === 'login';
  const isForgot = mode === 'forgot';

  // Clear stale token whenever the user flips between login/signup/forgot —
  // each Turnstile token is single-use, so a fresh widget must mint a new one.
  useEffect(() => { setCaptchaToken(''); }, [mode, role]);
  // Compute "current password" inline as a memoized derivation so we don't
  // re-call a render-time helper each pass. Using useMemo keeps the
  // react-hooks/set-state-in-effect linter satisfied (helper functions
  // returning state values during render trigger a false positive).
  const currentPassword = useMemo(() => {
    if (isLogin) return loginForm.password;
    if (role === 'admin') return adminForm.password;
    return playerForm.password;
  }, [isLogin, role, loginForm.password, adminForm.password, playerForm.password]);
  const pwStrong = useMemo(() => isPasswordStrong(currentPassword), [currentPassword]);

  useEffect(() => {
    if (isAdminAuth) navigate('/admin/dash');
    if (isPlayerAuth) navigate('/player/dash');
  }, [isAdminAuth, isPlayerAuth, navigate]);

  const handleUnifiedLogin = async (e) => {
    e.preventDefault(); setLoading(true);
    // Blur the active input *before* the async login work so iOS Safari
    // dismisses its keyboard and releases the keyboard-driven scroll offset
    // it preserves across the upcoming React Router navigation. Without this,
    // iOS keeps the page scrolled by ~30-50px on the destination dashboard.
    if (typeof document !== 'undefined' && document.activeElement?.blur) {
      document.activeElement.blur();
    }
    // Belt-and-braces: explicitly snap to top NOW, before the async work,
    // and again after the navigation completes via the shell's effect.
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
    }
    const loginEmail = loginForm.email;
    const loginPassword = loginForm.password;
    try {
      // Try admin login first
      await adminLogin(loginEmail, loginPassword, captchaToken);
      setPlayer(null);
      toast.success('Welcome back!');
    } catch (adminErr) {
      const detail = adminErr.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'EMAIL_NOT_CONFIRMED') {
        setUnconfirmed({ email: detail.email, role: 'admin' });
        setLoading(false);
        return;
      }
      try {
        // Fallback to player login
        await playerLogin(loginEmail, loginPassword, captchaToken);
        setAdminUser(null);
        toast.success('Welcome back!');
      } catch (playerErr) {
        const pdetail = playerErr.response?.data?.detail;
        if (pdetail && typeof pdetail === 'object' && pdetail.code === 'EMAIL_NOT_CONFIRMED') {
          setUnconfirmed({ email: pdetail.email, role: 'player' });
          setLoading(false);
          return;
        }
        toast.error(typeof pdetail === 'string' ? pdetail : 'Invalid email or password');
      }
    }
    setLoading(false);
  };

  const handleAdminSignup = async (e) => {
    e.preventDefault();
    if (!pwStrong) { toast.error('Please choose a stronger password'); return; }
    if (adminForm.password !== adminForm.confirm_password) {
      toast.error('Passwords do not match. Please re-enter the same password in both fields.');
      return;
    }
    if (inviteRequired && !inviteCode.trim()) {
      toast.error('SportSweeps is invite-only during soft-launch. Enter your invite code to continue.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/signup', {
        email: adminForm.email,
        password: adminForm.password,
        name: `${adminForm.first_name} ${adminForm.last_name}`.trim(),
        first_name: adminForm.first_name,
        last_name: adminForm.last_name,
        society_name: adminForm.society_name,
        cf_turnstile_response: captchaToken,
        invite_code: inviteCode.trim() || undefined,
      });
      setUnconfirmed({ email: adminForm.email, role: 'admin' });
      toast.success('Group created! Check your inbox to confirm your email.');
    } catch (err) { toast.error(err.response?.data?.detail || 'Signup failed'); }
    finally { setLoading(false); }
  };

  const handlePlayerSignup = async (e) => {
    e.preventDefault();
    if (!pwStrong) { toast.error('Please choose a stronger password'); return; }
    if (playerForm.password !== playerForm.confirm_password) {
      toast.error('Passwords do not match. Please re-enter the same password in both fields.');
      return;
    }
    setLoading(true);
    try {
      await api.post('/player/auth/signup', { email: playerForm.email, password: playerForm.password, first_name: playerForm.first_name, last_name: playerForm.last_name, display_name: playerForm.display_name || playerForm.first_name, cf_turnstile_response: captchaToken, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
      setUnconfirmed({ email: playerForm.email, role: 'player' });
      toast.success('Account created! Check your inbox to confirm your email.');
    } catch (err) { toast.error(err.response?.data?.detail || 'Signup failed'); }
    finally { setLoading(false); }
  };

  const handleResendConfirmation = async () => {
    if (!unconfirmed?.email) return;
    setResending(true);
    try {
      const endpoint = unconfirmed.role === 'admin' ? '/auth/resend-confirmation' : '/player/auth/resend-confirmation';
      await api.post(endpoint, { email: unconfirmed.email });
      toast.success('Confirmation email resent. Please check your inbox.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend. Try again in a minute.');
    } finally { setResending(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      // Try both admin and player reset — both return generic success messages
      await Promise.allSettled([
        api.post('/auth/forgot-password', { email: forgotEmail, cf_turnstile_response: captchaToken }),
        api.post('/player/auth/forgot-password', { email: forgotEmail, cf_turnstile_response: captchaToken }),
      ]);
      setForgotSent(true);
      toast.success('If an account exists, a reset link has been sent.');
    } catch (err) { toast.error(err.response?.data?.detail || 'Something went wrong'); }
    finally { setLoading(false); }
  };

  return (
    <div className="public-light-theme min-h-screen flex" style={FONT_B} data-testid="unified-auth-page">
      {/* Left — Brand Pane (FIXED on desktop) */}
      <div className="hidden lg:flex lg:w-[45%] lg:fixed lg:inset-y-0 lg:left-0 flex-col justify-between p-10 relative overflow-hidden" style={{ background: GRADIENT_BG }}>
        <div className="absolute inset-0 pointer-events-none" aria-hidden style={{
          WebkitMaskImage: 'url("/golfer-swing.webp")',
          WebkitMaskRepeat: 'repeat',
          WebkitMaskSize: '28px 28px',
          maskImage: 'url("/golfer-swing.webp")',
          maskRepeat: 'repeat',
          maskSize: '28px 28px',
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        }} />
        <div className="relative z-10">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-16" data-testid="auth-back-home">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </button>
          <div className="flex items-center gap-3 mb-8">
            <img src="/brand/landscape-logo-white.png" alt="SportSweeps" className="h-12 w-auto" />
          </div>
          <h2 className="text-3xl xl:text-4xl font-extrabold text-white tracking-tight leading-tight mb-4" style={FONT_H}>
            Fantasy pools,<br />done right.
          </h2>
          <p className="text-white/60 text-base leading-relaxed max-w-sm" style={FONT_B}>
            Live scoring, TV leaderboards, automated team management. Everything your group needs.
          </p>
        </div>
        {/* "Trusted by golf groups..." footer removed per brand request. */}
      </div>

      {/* Right — Scrollable Auth Form */}
      <div className="flex-1 lg:ml-[45%] min-h-screen overflow-y-auto">
        <div className="flex flex-col items-center justify-start lg:justify-center min-h-screen px-6 sm:px-12 py-6 lg:py-10 bg-white">
          <div className="w-full max-w-md">
            {/* Mobile logo */}
            <div className="lg:hidden flex items-center gap-2 mb-6">
              <button onClick={() => navigate('/')} className="mr-2 w-9 h-9 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-600"><ArrowLeft className="w-5 h-5" /></button>
              <img src="/brand/landscape-logo.png" alt="SportSweeps" className="h-8 w-auto" />
            </div>

            {/* Forgot Password View */}
            {unconfirmed ? (
              <UnconfirmedInboxView
                email={unconfirmed.email}
                resending={resending}
                onResend={handleResendConfirmation}
                onBack={() => { setUnconfirmed(null); setMode('login'); }}
              />
            ) : isForgot ? (
              <ForgotPasswordView
                email={forgotEmail}
                setEmail={setForgotEmail}
                loading={loading}
                onSubmit={handleForgotPassword}
                sent={forgotSent}
                onBackToLogin={() => { setMode('login'); setForgotSent(false); }}
              />
            ) : (
              <>
                <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-2 text-gray-900" style={FONT_H}>
                  {isLogin ? 'Welcome back' : 'Create your account'}
                </h1>
                <p className="text-gray-500 text-sm mb-6 lg:mb-8" style={FONT_B}>
                  {isLogin ? 'Sign in to your account to continue.' : 'Get started with SportSweeps today.'}
                </p>

                {/* Role Toggle — only on signup */}
                {!isLogin && (
                  <div className="flex bg-gray-100 rounded-xl p-1 mb-8" data-testid="auth-role-toggle">
                    {[{ id: 'player', label: "I'm a Player" }, { id: 'admin', label: "I'm an Admin" }].map(r => (
                      <button key={r.id} onClick={() => setRole(r.id)}
                        className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all ${role === r.id ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                        style={FONT_B} data-testid={`auth-role-${r.id}`}>
                        {r.label}
                      </button>
                    ))}
                  </div>
                )}

                {/* Form */}
                <form onSubmit={getSubmitHandler({ isLogin, role, handleUnifiedLogin, handleAdminSignup, handlePlayerSignup })} className="space-y-4">
                  {/* Honeypot — hidden from real users, bots fill it */}
                  <div className="absolute opacity-0 pointer-events-none h-0 overflow-hidden" aria-hidden="true" tabIndex={-1}>
                    <input type="text" name="website" autoComplete="off" tabIndex={-1} />
                  </div>
                  {!isLogin && role === 'admin' && (
                    <>
                      <AdminSignupFields form={adminForm} setForm={setAdminForm} />
                      {inviteRequired && (
                        <div data-testid="admin-invite-code-block">
                          <FieldInput
                            label="Invite code"
                            value={inviteCode}
                            onChange={setInviteCode}
                            testId="auth-admin-invite-code"
                            required
                            placeholder="SM-XXXX-XXXX"
                          />
                          <p className="text-[11px] text-gray-500 mt-1.5" style={FONT_B}>
                            SportSweeps is invite-only during soft-launch. Don&apos;t have a code?{' '}
                            <button
                              type="button"
                              onClick={() => openSupportBubble('messages')}
                              className="font-semibold hover:underline cursor-pointer bg-transparent border-0 p-0"
                              style={{ color: PRIMARY }}
                              data-testid="admin-request-access-button"
                            >
                              Request access
                            </button>.
                          </p>
                        </div>
                      )}
                    </>
                  )}
                  {!isLogin && role === 'player' && <PlayerSignupFields form={playerForm} setForm={setPlayerForm} />}

                  {isLogin ? (
                    <>
                      <FieldInput label="Email" type="email" value={loginForm.email}
                        onChange={v => setLoginForm(p => ({ ...p, email: v }))} testId="auth-email" required />
                      <PasswordField
                        value={loginForm.password}
                        onChange={v => setLoginForm(p => ({ ...p, password: v }))}
                        show={showPw}
                        onToggleShow={() => setShowPw(!showPw)}
                        testId="auth-password"
                        required
                      />
                      <div className="flex justify-end">
                        <button type="button" onClick={() => { setMode('forgot'); setForgotEmail(loginForm.email); }}
                          className="text-xs font-semibold hover:underline" style={{ color: PRIMARY }} data-testid="auth-forgot-link">
                          Forgot password?
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <FieldInput label="Email" type="email" value={role === 'admin' ? adminForm.email : playerForm.email}
                        onChange={v => role === 'admin' ? setAdminForm(p => ({ ...p, email: v })) : setPlayerForm(p => ({ ...p, email: v }))} testId="auth-email" required />
                      <div>
                        <PasswordField
                          value={currentPassword}
                          onChange={v => role === 'admin' ? setAdminForm(p => ({ ...p, password: v })) : setPlayerForm(p => ({ ...p, password: v }))}
                          show={showPw}
                          onToggleShow={() => setShowPw(!showPw)}
                          testId="auth-password"
                          required
                        />
                        <PasswordStrength password={currentPassword} />
                      </div>
                      <FieldInput
                        label="Confirm password"
                        type={showPw ? 'text' : 'password'}
                        value={role === 'admin' ? adminForm.confirm_password : playerForm.confirm_password}
                        onChange={v => role === 'admin'
                          ? setAdminForm(p => ({ ...p, confirm_password: v }))
                          : setPlayerForm(p => ({ ...p, confirm_password: v }))}
                        testId="auth-confirm-password"
                        required
                        placeholder="Re-enter the same password"
                      />
                    </>
                  )}

                  {/* Cloudflare Turnstile widget removed from all auth flows
                      per user request — bot protection is now handled at the
                      Cloudflare WAF edge (Security → Bot Fight Mode). */}
                  <button
                    type="submit"
                    disabled={loading || (!isLogin && !pwStrong)}
                    className="w-full rounded-xl font-bold py-3.5 text-white text-sm transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ ...FONT_B, background: GRADIENT_BG }}
                    data-testid="auth-submit"
                  >
                    {getSubmitButtonLabel({ loading, isLogin, captchaPending: false })}
                  </button>
                </form>

                <div className="mt-6 text-center">
                  <button onClick={() => setMode(isLogin ? 'signup' : 'login')} className="text-sm text-gray-500 hover:text-gray-700 transition-colors" style={FONT_B} data-testid="auth-toggle-mode">
                    {isLogin ? "Don't have an account? " : 'Already have an account? '}
                    <span className="font-semibold" style={{ color: PRIMARY }}>{isLogin ? 'Sign up' : 'Sign in'}</span>
                  </button>
                </div>

                {/* Access Code Section */}
                <AccessCodeSection />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
